import {
  AnswerAreaPinComponent,
  AudioPinComponentRenderer,
  boxFromLayout,
  CategoryAreaPinComponent,
  DropZonePinComponentRenderer,
  EditorTagWrapper,
  HotspotPinComponentRenderer,
  ImagePinComponentLoadingPlaceholder,
  ImagePinComponentRenderer,
  PinComponentSwitchDefaultRendererMemo,
  PinComponentSwitchRendererProps,
  ShapePinComponentRenderer,
  TextPinComponentRenderer,
  usePinContext,
  VideoPinComponentRenderer,
} from '@lessonup/pin-renderer';
import {
  isDragAndDropItemPinComponent,
  isDragAndDropPin,
  isTextPinComponent,
  PinComponent,
} from '@lessonup/pins-shared';
import React, { memo } from 'react';
import { useEditorTranslation } from '../../../hooks/useEditorTranslation';
import { TipTapCollaborative } from '../../../utils/yjs/TipTapCollaborative';
import { TipTapFillInTheBlanks } from '../../../utils/yjs/fillInTheBlanks/TipTapFillInTheBlanks';
import { useEditorContextSidebar } from '../../EditorContextSidebar/hooks/useEditorContextSidebar';
import { AnswerAreaContentSwitchEditor } from './AnswerAreaPinComponent/AnswerAreaContentSwitchEditor';
import { FillInTheBlanksAreaEditorPinComponent } from './FillInTheBlanksAreaPinComponent/FillInTheBlanksAreaPinComponentEditor';
import { getPlaceholderTranslation } from './utils/placeholder';

export type PinComponentLoadingSwitchRendererEditorProps = {
  pinComponent: PinComponent;
  pinId: string;
  scale: number;
};

export const PinComponentLoadingSwitchRendererEditor: React.FC<PinComponentLoadingSwitchRendererEditorProps> = ({
  pinComponent,
  pinId,
  scale,
}) => {
  switch (pinComponent.type) {
    case 'IMAGE':
      return <ImagePinComponentLoadingPlaceholder />;
    case 'CATEGORY_AREA':
      return (
        <CategoryAreaPinComponent
          settings={pinComponent.settings}
          componentId={pinComponent.id}
          pinId={pinId}
          editable={false}
          scale={scale}
          active={false}
        />
      );
    default:
      return null;
  }
};

export const PinComponentLoadingSwitchRendererEditorMemo = memo(PinComponentLoadingSwitchRendererEditor);

const PinComponentSwitchRendererEditor: React.FC<PinComponentSwitchRendererProps> = (props) => {
  const { pinComponent } = props;
  const { t } = useEditorTranslation();
  const pin = usePinContext();
  const box = boxFromLayout(pinComponent.layout);
  const { callAction } = useEditorContextSidebar();

  const showTag =
    (isDragAndDropPin(pin) && isDragAndDropItemPinComponent(pinComponent, pin.settings.linkedPinComponents)) ?? false;

  const showTagWithBorder = showTag && isTextPinComponent(pinComponent);

  const component = (() => {
    switch (pinComponent.type) {
      case 'IMAGE':
        return (
          <ImagePinComponentRenderer
            settings={pinComponent.settings}
            layout={box}
            isThumb={props.isThumb}
            onDoubleClick={() => props.setModeCropping && props.setModeCropping(pinComponent)}
            onPlaceholderDoubleClick={() => (props.active ? callAction('settings', 'replaceImage') : undefined)}
          />
        );
      case 'TEXT':
        return (
          <TextPinComponentRenderer
            componentId={pinComponent.id}
            settings={pinComponent.settings}
            editable={props.editable}
            pinId={props.pinId}
            active={props.active}
            Renderer={TipTapCollaborative}
            isTextEditMode={props.isTextEditMode}
            setModeTextEdit={props.setModeTextEdit}
            placeholder={t(
              getPlaceholderTranslation(props.pinType, pinComponent.id, props.layoutKey, props.layoutParts)
            )}
          />
        );
      case 'HOTSPOT':
        return (
          <HotspotPinComponentRenderer
            pinComponent={pinComponent}
            active={props.active}
            pinId={props.pinId}
            Renderer={TipTapCollaborative}
          />
        );
      case 'FILL_IN_THE_BLANKS_AREA':
        return (
          <FillInTheBlanksAreaEditorPinComponent
            settings={pinComponent.settings}
            active={props.active}
            pinId={props.pinId}
            pinComponentId={pinComponent.id}
            Renderer={TipTapFillInTheBlanks}
            isTextEditMode={props.isTextEditMode}
            setModeTextEdit={
              props.setModeTextEdit
                ? (textPinComponentId: string) => props.setModeTextEdit!(textPinComponentId)
                : undefined
            }
            scale={props.scale}
            editable={props.editable}
          />
        );
      case 'VIDEO':
        return (
          <VideoPinComponentRenderer
            settings={pinComponent.settings}
            componentId={pinComponent.id}
            scale={props.scale}
            width={box.size.width}
            height={box.size.height}
            editable={props.editable}
            active={props.active}
            isThumb={props.isThumb}
            onPlaceholderDoubleClick={() => (props.active ? callAction('settings', 'replaceVideo') : undefined)}
          />
        );
      case 'ANSWER_AREA':
        return (
          <AnswerAreaPinComponent settings={pinComponent.settings}>
            <AnswerAreaContentSwitchEditor
              isPinComponentActive={props.active}
              editable={props.editable}
              scale={props.scale}
              isInteractive={!props.isThumb}
            />
          </AnswerAreaPinComponent>
        );
      case 'CATEGORY_AREA':
        return (
          <CategoryAreaPinComponent
            settings={pinComponent.settings}
            componentId={pinComponent.id}
            pinId={props.pinId}
            active={props.active}
            editable={props.editable}
            scale={props.scale}
            activeCategoryId={props.activeCategoryId}
            onMouseDown={props.onMouseDown}
            isTextEditMode={props.isTextEditMode}
            setModeCategoryEdit={props.setModeCategoryEdit}
            setModeTextEdit={props.setModeTextEdit}
            Renderer={TipTapCollaborative}
          />
        );

      case 'AUDIO':
        return (
          <AudioPinComponentRenderer
            pinId={props.pinId}
            pinComponent={pinComponent}
            disableAllClicks={!props.active}
            disablePlayOnClick={true}
            Renderer={TipTapCollaborative}
            active={props.active}
            isThumb={props.isThumb}
          />
        );

      case 'DROP_ZONE':
        return (
          <DropZonePinComponentRenderer
            componentId={pinComponent.id}
            settings={pinComponent.settings}
            pinId={props.pinId}
            active={props.active}
            editable={props.editable}
            isLinked={false}
            Renderer={TipTapCollaborative}
            isTextEditMode={props.isTextEditMode}
            setModeTextEdit={props.setModeTextEdit}
          />
        );

      case 'SHAPE':
        return <ShapePinComponentRenderer pinComponent={pinComponent} />;
    }
    return <PinComponentSwitchDefaultRendererMemo {...props} />;
  })();

  return showTag ? (
    <EditorTagWrapper id={pinComponent.id} labelFormat="number" withBorder={showTagWithBorder}>
      {component}
    </EditorTagWrapper>
  ) : (
    component
  );
};

export const PinComponentSwitchRendererEditorMemo = memo(PinComponentSwitchRendererEditor);
